import {
  WALLET_STATUS_LABELS_LAO,
  type WalletAccountListRow,
} from "@/modules/wallet/domain/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { CompactNumber } from "@/shared/ui/CompactNumber";
import { DataTable, createSortableColumn, type TanstackReactTable } from "@/shared/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";


import { BookOpenIcon, SettingsIcon } from "lucide-react";

const STATUS_BADGE_VARIANTS: Record<
  WalletAccountListRow["status"],
  "default" | "secondary" | "destructive" | "outline"
> = {
  ACTIVE: "default",
  SUSPENDED: "secondary",
  CLOSED: "destructive",
};

type WalletAccountsTableProps = {
  data: WalletAccountListRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onViewLedger: (walletId: string) => void;
  onSetLimits: (walletId: string) => void;
};

export function WalletAccountsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onPaginationChange,
  onViewLedger,
  onSetLimits,
}: WalletAccountsTableProps) {
  const columns: TanstackReactTable.ColumnDef<WalletAccountListRow>[] = [
    {
      accessorKey: "customerName",
      header: "ລູກຄ້າ",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {row.original.customerName ?? "–"}
          </span>
          <span className="text-muted-foreground text-xs">
            {row.original.customerPhone}
          </span>
        </div>
      ),
      size: 180,
      enableSorting: false,
    },
    {
      accessorKey: "id",
      header: "Wallet ID",
      cell: ({ row }) => (
        <span className="font-mono text-muted-foreground text-sm">
          {row.original.id.slice(0, 10)}…
        </span>
      ),
      size: 120,
      enableSorting: false,
    },
    {
      accessorKey: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge variant={STATUS_BADGE_VARIANTS[row.original.status]}>
          {WALLET_STATUS_LABELS_LAO[row.original.status]}
        </Badge>
      ),
      size: 100,
      enableSorting: false,
    },
    createSortableColumn<WalletAccountListRow>("balanceLak", "ຍອດ (LAK)", {
      size: 120,
      cell: ({ row }) => (
        <CompactNumber
          value={Number(row.original.balanceLak)}
          formatOptions={{
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }}
        />
      ),
    }),
    createSortableColumn<WalletAccountListRow>("createdAt", "ວັນທີສ້າງ", {
      size: 160,
      cell: ({ row }) => formatDateTimeLocal(row.original.createdAt),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewLedger(row.original.id)}
            title="ເບິ່ງ Ledger"
          >
            <BookOpenIcon className="size-4" />
            ເບິ່ງ Ledger
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onSetLimits(row.original.id)}
            title="ຕັ້ງວົງເງິນ"
          >
            <SettingsIcon className="size-4" />
            ຕັ້ງວົງເງິນ
          </Button>
        </div>
      ),
      size: 220,
      enableSorting: false,
    },
  ];

  return (
    <DataTable<WalletAccountListRow, unknown>
      noDataMessage="ບໍ່ພົບບັນຊີ wallet"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
